import { useState } from 'react';
import { RoundedButton } from "../button";
import TopHeader from "../misc/topHeader.tsx";
import { ArrowRight, Flame, Droplets, Mountain, Wind, Sword, Shield, Trophy, Users, Gamepad2 } from "lucide-react";
import { motion } from "framer-motion";

export default function HowItWorks() {
  const [activeStep, setActiveStep] = useState(1);
  
  const steps = [
    {
      title: "Choose Your Element",
      description: "Select your elemental warrior from Fire, Water, Earth, or Air. Each element has unique strengths and abilities that affect your battle strategy.",
      icon: <div className="flex gap-2">
        <Flame className="w-6 h-6 text-red-500" />
        <Droplets className="w-6 h-6 text-blue-500" />
        <Mountain className="w-6 h-6 text-amber-500" />
        <Wind className="w-6 h-6 text-emerald-500" />
      </div>,
      features: [
        "Unique elemental abilities",
        "Special character traits",
        "Strategic advantages",
        "Custom battle animations"
      ],
      image: "/screens/mint.png"
    },
    {
      title: "Enter The Arena",
      description: "Join the knockout tournament where strategy meets destiny. Choose your opponents wisely and plan your path to victory.",
      icon: <div className="flex gap-2">
        <Sword className="w-8 h-8 text-primary-500" />
        <Gamepad2 className="w-8 h-8 text-amber-500" />
      </div>,
      features: [
        "Tournament brackets",
        "Strategic matchmaking",
        "Real-time battles",
        "Interactive combat"
      ],
      image: "/screens/areana.png"
    },
    {
      title: "Battle For Glory",
      description: "Engage in epic battles where your elemental powers and tactical decisions determine the outcome. Each victory brings you closer to the championship.",
      icon: <div className="flex gap-2">
        <Shield className="w-8 h-8 text-amber-500" />
        <Sword className="w-8 h-8 text-primary-500 rotate-45" />
      </div>,
      features: [
        "Dynamic combat system",
        "Element interactions",
        "Power-up opportunities",
        "Battle rewards"
      ],
      image: "/screens/battle.png"
    },
    {
      title: "Claim Victory",
      description: "Rise through the ranks, defeat your opponents, and become the ultimate elemental champion. Winners receive exclusive rewards and recognition.",
      icon: <Trophy className="w-8 h-8 text-yellow-500" />,
      features: [
        "Championship titles",
        "Exclusive rewards",
        "Leaderboard rankings",
        "Special abilities unlock"
      ],
      image: "/images/steps/step4.jpg"
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white px-8 py-16 font-sans relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-20%,rgba(99,102,241,0.15),rgba(0,0,0,0)_70%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_60%,rgba(245,158,11,0.1),rgba(0,0,0,0)_50%)]" />
      
      <div className="container relative z-10 mx-auto">
        <div className="text-center mb-16">
          <TopHeader title="How It Works" />
          <p className="mt-6 text-gray-400 text-lg max-w-2xl mx-auto code-mono">
            Join the ultimate elemental battle arena where strategy, skill, and mystical powers collide.
            Follow these steps to begin your journey to becoming the champion.
          </p>
        </div>

        {/* Steps Navigation */}
        <div className="flex justify-center gap-4 mb-12 overflow-x-auto pb-4">
          {steps.map((step, index) => (
            <button
              key={index}
              className={`group flex items-center gap-2 px-6 py-3 rounded-xl transition-all duration-300
                ${activeStep === index + 1 
                  ? 'bg-gradient-to-r from-primary-500/20 to-amber-500/20 border border-primary-500/30' 
                  : 'hover:bg-gray-800/50'}
                whitespace-nowrap code-mono`}
              onClick={() => setActiveStep(index + 1)}
            >
              <span className={`w-8 h-8 rounded-full flex items-center justify-center code-mono
                ${activeStep === index + 1 
                  ? 'bg-gradient-to-r from-primary-500 to-amber-500 text-white' 
                  : 'bg-gray-800 text-gray-400 group-hover:text-white'}
                transition-all duration-300`}>
                {index + 1}
              </span>
              <span className={`font-medium code-mono ${activeStep === index + 1 ? 'text-primary-400' : 'text-gray-400'}`}>
                {step.title}
              </span>
            </button>
          ))}
        </div>

        {/* Active Step Content */}
        <motion.div
          key={activeStep}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid md:grid-cols-2 gap-12 items-center"
        >
          {/* Left Content */}
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-gray-800/50 to-transparent p-6 rounded-2xl
              border border-gray-700/50 backdrop-blur-sm">
              <div className="mb-4">{steps[activeStep - 1].icon}</div>
              <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary-400 to-amber-400 
                bg-clip-text text-transparent code-mono">
                {steps[activeStep - 1].title}
              </h3>
              <p className="text-gray-300 leading-relaxed code-mono">
                {steps[activeStep - 1].description}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {steps[activeStep - 1].features.map((feature, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="flex items-center gap-3 p-3 rounded-xl
                    bg-gradient-to-r from-gray-800/30 to-transparent
                    border border-gray-700/30 hover:border-primary-500/30 
                    transition-all duration-300 group"
                >
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-primary-500 to-amber-500
                    group-hover:scale-150 transition-transform duration-300" />
                  <span className="text-gray-300 group-hover:text-primary-400 transition-colors duration-300 code-mono">
                    {feature}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Right Content - Interactive Visual */}
          <motion.div 
            className="relative group"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-primary-500 to-amber-500 rounded-2xl 
              blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-500" />
            <div className="relative bg-gray-900/90 rounded-2xl p-6 backdrop-blur-sm border border-white/5
              shadow-xl shadow-black/20">
              <div className="rounded-xl overflow-hidden bg-gray-800 relative">
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 
                  group-hover:opacity-100 transition-opacity duration-500" />
                <img 
                  src={steps[activeStep - 1].image}
                  alt={steps[activeStep - 1].title}
                  className="w-full h-full object-contain transform transition-all duration-500 
                    group-hover:scale-105"
                />
              </div>
              
              {/* Screenshot Label */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full 
                bg-gray-900/90 backdrop-blur-sm border border-white/10
                opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 
                group-hover:translate-y-0">
                <span className="text-sm text-gray-300 code-mono">
                  {activeStep === 1 ? 'Mint Interface' : 
                   activeStep === 2 ? 'Arena Dashboard' : 
                   activeStep === 3 ? 'Battle Scene' : 'Victory Screen'}
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Community Section */}
        <div className="mt-20 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r 
            from-primary-500/20 to-amber-500/20 border border-primary-500/30">
            <Users className="w-5 h-5 text-primary-400" />
            <span className="text-gray-300 code-mono">Join our growing community of warriors</span>
          </div>
          <div className="mt-6 flex justify-center gap-4">
            <RoundedButton>
              <div className="flex items-center gap-2 code-mono">
                Start Your Journey <ArrowRight className="w-4 h-4" />
              </div>
            </RoundedButton>
          </div>
        </div>
      </div>
    </div>
  );
}
